import { atom } from "jotai";
import type { AtomicRange } from "./StreamHandler.ts";

/**
 * One reply's accumulated live stream: the concatenated text plus the atomic
 * char-ranges (spans to reveal all at once, e.g. links) in cumulative
 * coordinates, ascending and disjoint — exactly what `StreamHandler` consumes.
 */
export type StreamReplyBuffer = {
  text: string;
  atomicRanges: AtomicRange[];
};

/**
 * Live reply-stream buffers keyed by `userResponse` artifact id (cumulative text
 * + atomic ranges). Socket handlers write it; the streaming leaf reads its own
 * slice via `selectAtom`, so a delta re-renders only that leaf. Entries are
 * never cleared (a finalized reply's leaf ignores its buffer) and never persist.
 */
export const streamReplyBuffersAtom = atom<Record<string, StreamReplyBuffer>>({});

/**
 * Fold one `thread_event:message_stream` frame into a reply's cumulative buffer:
 * append the frame's `delta` and shift its frame-local `atomicRanges` by the
 * prior text length into cumulative coordinates. A frame without ranges just
 * appends its text.
 */
export function appendStreamDelta(
  entry: StreamReplyBuffer | undefined,
  delta: string,
  atomicRanges: readonly AtomicRange[] = [],
): StreamReplyBuffer {
  const base = entry ?? { text: "", atomicRanges: [] };
  const offset = base.text.length;
  return {
    text: base.text + delta,
    atomicRanges: [
      ...base.atomicRanges,
      ...atomicRanges.map((r) => ({ start: r.start + offset, end: r.end + offset })),
    ],
  };
}
